/**
 * Componente de Gráfico de Faixa Etária (Bar Chart)
 * Seguindo Single Responsibility Principle
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { AgeGroupStats } from '../../domain/entities/Analytics';

interface AgeDistributionChartProps {
  data: AgeGroupStats;
}

const AGE_LABELS = {
  age_8_12: '👶 Crianças (8-12)',
  age_15_20: '👦 Adolescentes (15-20)',
  age_25_32: '👨 Jovens (25-32)',
  age_adults: '👔 Adultos (38-53)',
  age_60_plus: '👴 Idosos (60+)',
};

/**
 * Gráfico de Barras para distribuição de faixa etária
 */
export const AgeDistributionChart: React.FC<AgeDistributionChartProps> = ({ data }) => {
  const chartData = [
    {
      name: AGE_LABELS.age_8_12,
      pessoas: data.age_8_12 || 0,
      percentual: data.age_8_12_percentage || 0,
    },
    {
      name: AGE_LABELS.age_15_20,
      pessoas: data.age_15_20 || 0,
      percentual: data.age_15_20_percentage || 0,
    },
    {
      name: AGE_LABELS.age_25_32,
      pessoas: data.age_25_32 || 0,
      percentual: data.age_25_32_percentage || 0,
    },
    {
      name: AGE_LABELS.age_adults,
      pessoas: data.age_adults || 0,
      percentual: data.age_adults_percentage || 0,
    },
    {
      name: AGE_LABELS.age_60_plus,
      pessoas: data.age_60_plus || 0,
      percentual: data.age_60_plus_percentage || 0,
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">
        Distribuição por Faixa Etária
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis 
            dataKey="name" 
            angle={-15} 
            textAnchor="end" 
            height={80}
            stroke="#9CA3AF"
            tick={{ fill: '#9CA3AF' }}
          />
          <YAxis 
            stroke="#9CA3AF"
            tick={{ fill: '#9CA3AF' }}
          />
          <Tooltip
            formatter={(value: number, name: string, props: any) => [
              `${value} pessoas (${props.payload.percentual.toFixed(1)}%)`,
              'Quantidade',
            ]}
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '8px',
            }}
            wrapperClassName="dark:text-gray-900"
          />
          <Legend wrapperStyle={{ color: 'inherit' }} />
          <Bar dataKey="pessoas" fill="#8B5CF6" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 grid grid-cols-5 gap-2 text-center">
        <div>
          <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{data.age_8_12 || 0}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Crianças</p>
        </div>
        <div>
          <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{data.age_15_20 || 0}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Adolescentes</p>
        </div>
        <div>
          <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{data.age_25_32 || 0}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Jovens</p>
        </div>
        <div>
          <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">{data.age_adults || 0}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Adultos</p>
        </div>
        <div>
          <p className="text-xl font-bold text-orange-600">{data.age_60_plus || 0}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Idosos</p>
        </div>
      </div>
    </div>
  );
};
